import { useState, useEffect, useCallback } from "react";
import { message, Modal } from "antd";
import { TokenService, TokenInfo } from "../service-apis/tokenService";

export interface UseServiceTokenProps {
  serviceId?: string;
  serviceName?: string;
  visible: boolean;
  onTokenChange?: () => void; // 添加回调函数，用于通知父组件刷新数据
}

export const useServiceToken = ({ serviceId, serviceName, visible, onTokenChange }: UseServiceTokenProps) => {
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // 获取Token信息
  const fetchTokenInfo = useCallback(async () => {
    if (!serviceId) return;

    try {
      setSubmitting(true);
      const response = await TokenService.fetchTokenInfo(serviceId);
      console.log("🔍 [useServiceToken.fetchTokenInfo] API响应:", {
        response,
        data: response.data,
        allowedAudience: response.data?.allowedAudience,
        allowed_audience: response.data?.allowed_audience,
      });
      if (response.success) {
        console.log("🔍 [useServiceToken.fetchTokenInfo] 设置tokenInfo:", {
          tokenInfo: response.data,
          allowedAudience: response.data?.allowedAudience,
          allowed_audience: response.data?.allowed_audience,
        });
        setTokenInfo(response.data);
      } else {
        setTokenInfo(null);
      }
    } catch (error) {
      console.error("获取Token信息失败:", error);
      setTokenInfo(null);
    } finally {
      setSubmitting(false);
    }
  }, [serviceId]);

  // 创建Token
  const createToken = useCallback(
    async (values: any): Promise<boolean> => {
      if (!serviceId) return false;

      try {
        setSubmitting(true);
        // 处理 expires_at：如果是 dayjs 对象则格式化，如果是字符串则直接使用，空值则设为 null
        let expiresAt: string | null = null;
        if (values.expires_at) {
          if (typeof values.expires_at === "string") {
            expiresAt = values.expires_at.trim() || null;
          } else if (values.expires_at && typeof values.expires_at.format === "function") {
            // dayjs 对象，格式化为 "YYYY-MM-DD HH:mm:ss"
            expiresAt = values.expires_at.format("YYYY-MM-DD HH:mm:ss");
          } else if (values.expires_at && typeof values.expires_at.toISOString === "function") {
            // Date 对象，转换为 "YYYY-MM-DD HH:mm:ss" 格式
            expiresAt = values.expires_at.toISOString().replace("T", " ").substring(0, 19);
          } else {
            // 其他类型，尝试转换为字符串
            expiresAt = String(values.expires_at);
          }
        }

        // 处理 max_usage：空字符串或 undefined 转为 null，否则转为数字
        let maxUsage: number | null = null;
        if (values.max_usage !== undefined && values.max_usage !== null && values.max_usage !== "") {
          const numValue = Number(values.max_usage);
          if (!isNaN(numValue) && numValue > 0) {
            maxUsage = numValue;
          }
        }

        // 处理 ip_whitelist：空字符串转为 null
        const ipWhitelist = values.ip_whitelist && typeof values.ip_whitelist === "string"
          ? values.ip_whitelist.trim() || null
          : values.ip_whitelist || null;

        // 处理 description：空字符串转为 null
        const description = values.description && typeof values.description === "string"
          ? values.description.trim() || null
          : values.description || null;

        // 验证target_service是否存在
        if (!values.target_service || typeof values.target_service !== "string") {
          message.error("目标服务名称(target_service)是必填项");
          return false;
        }

        const params = {
          target_service: values.target_service,
          expires_at: expiresAt,
          is_active: values.is_active !== undefined ? values.is_active : true,
          max_usage: maxUsage,
          ip_whitelist: ipWhitelist,
          description: description,
        };

        console.log("🔍 [创建Token] 请求参数:", params);
        console.log("🔍 [创建Token] serviceId:", serviceId);
        console.log("🔍 [创建Token] 准备调用 TokenService.createToken");
        const response = await TokenService.createToken(serviceId, params);
        console.log("🔍 [创建Token] 响应结果:", response);
        if (response.success) {
          message.success("Token创建成功");
          setTokenInfo(response.data);
          // 通知父组件刷新服务列表
          onTokenChange?.();
          return true;
        } else {
          message.error(response.message || "Token创建失败");
          return false;
        }
      } catch (error) {
        console.error("创建Token失败:", error);
        message.error("创建Token失败");
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [serviceId, onTokenChange]
  );

  // 更新Token
  const updateToken = useCallback(
    async (values: any): Promise<boolean> => {
      if (!tokenInfo || !serviceId) return false;

      try {
        setSubmitting(true);
        // 处理 expires_at：如果是 dayjs 对象则格式化，如果是字符串则直接使用，空值则设为 null
        let expiresAt: string | null = null;
        if (values.expires_at) {
          if (typeof values.expires_at === "string") {
            expiresAt = values.expires_at.trim() || null;
          } else if (values.expires_at && typeof values.expires_at.format === "function") {
            // dayjs 对象，格式化为 "YYYY-MM-DD HH:mm:ss"
            expiresAt = values.expires_at.format("YYYY-MM-DD HH:mm:ss");
          } else if (values.expires_at && typeof values.expires_at.toISOString === "function") {
            // Date 对象，转换为 "YYYY-MM-DD HH:mm:ss" 格式
            expiresAt = values.expires_at.toISOString().replace("T", " ").substring(0, 19);
          } else {
            // 其他类型，尝试转换为字符串
            expiresAt = String(values.expires_at);
          }
        }

        // 处理 max_usage：空字符串或 undefined 转为 null，否则转为数字
        let maxUsage: number | null = null;
        if (values.max_usage !== undefined && values.max_usage !== null && values.max_usage !== "") {
          const numValue = Number(values.max_usage);
          if (!isNaN(numValue) && numValue > 0) {
            maxUsage = numValue;
          }
        }

        // 处理 ip_whitelist：空字符串转为 null
        const ipWhitelist = values.ip_whitelist && typeof values.ip_whitelist === "string"
          ? values.ip_whitelist.trim() || null
          : values.ip_whitelist || null;

        // 处理 description：空字符串转为 null
        const description = values.description && typeof values.description === "string"
          ? values.description.trim() || null
          : values.description || null;

        const params = {
          expires_at: expiresAt,
          is_active: values.is_active !== undefined ? values.is_active : true,
          max_usage: maxUsage,
          ip_whitelist: ipWhitelist,
          description: description,
          // 注意：target_service不能更新，因为它是唯一约束的一部分
        };

        console.log("[更新Token] 请求参数:", params);
        const response = await TokenService.updateToken(serviceId, tokenInfo.id, params);
        console.log("[更新Token] 响应结果:", response);
        if (response.success) {
          message.success("Token更新成功");
          setTokenInfo(response.data);
          return true;
        } else {
          message.error(response.message || "Token更新失败");
          return false;
        }
      } catch (error) {
        console.error("更新Token失败:", error);
        message.error("更新Token失败");
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [tokenInfo, serviceId]
  );

  // 刷新Token
  const refreshToken = useCallback(async (): Promise<void> => {
    if (!tokenInfo || !serviceId) return;

    try {
      setSubmitting(true);
      const response = await TokenService.refreshToken(serviceId, tokenInfo.id);
      if (response.success) {
        message.success("Token刷新成功");
        setTokenInfo(response.data);
      } else {
        message.error(response.message || "Token刷新失败");
      }
    } catch (error) {
      console.error("刷新Token失败:", error);
      message.error("刷新Token失败");
    } finally {
      setSubmitting(false);
    }
  }, [tokenInfo, serviceId]);

  // 删除Token
  const deleteToken = useCallback(async (): Promise<void> => {
    if (!tokenInfo || !serviceId) return;

    Modal.confirm({
      title: "确认删除",
      content: "确定要删除这个Token吗？删除后无法恢复。",
      onOk: async () => {
        try {
          setSubmitting(true);
          const response = await TokenService.deleteToken(serviceId, tokenInfo.id);
          if (response.success) {
            message.success("Token删除成功");
            setTokenInfo(null);
            // 通知父组件刷新服务列表
            onTokenChange?.();
          } else {
            message.error(response.message || "Token删除失败");
          }
        } catch (error) {
          console.error("删除Token失败:", error);
          message.error("删除Token失败");
        } finally {
          setSubmitting(false);
        }
      },
    });
  }, [tokenInfo, serviceId, onTokenChange]);

  // 复制Token
  const copyToken = useCallback(async (): Promise<void> => {
    const tokenValue = tokenInfo?.tokenValue || tokenInfo?.token_value;

    if (!tokenValue) {
      message.warning("暂无可复制的 Token");
      return;
    }

    const performCopy = async () => {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
        await navigator.clipboard.writeText(tokenValue);
        return true;
      }

      const textarea = document.createElement("textarea");
      textarea.value = tokenValue;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      const result = document.execCommand("copy");
      document.body.removeChild(textarea);
      return result;
    };

    try {
      const copied = await performCopy();
      if (copied) {
        message.success("Token已复制到剪贴板");
      } else {
        throw new Error("copy command returned false");
      }
    } catch (error) {
      console.error("复制Token失败:", error);
      message.error("复制失败，请手动复制");
    }
  }, [tokenInfo]);

  // 当visible变化时获取Token信息
  useEffect(() => {
    if (visible && serviceId) {
      fetchTokenInfo();
    }
  }, [visible, serviceId, fetchTokenInfo]);

  return {
    tokenInfo,
    submitting,
    fetchTokenInfo,
    createToken,
    updateToken,
    refreshToken,
    deleteToken,
    copyToken,
  };
};
